/**
 * settings.ts — local device settings for the SAGE Platform shell.
 *
 * The MCP workers each have their OWN bearer (MCP_BEARER_* per worker, the
 * 2026-05-26 lockdown), so tokens are stored as a per-service map under
 * 'sage_mcp_tokens'. A legacy single token under 'sage_session_token' (the
 * sage-companion key) is honored as a fallback for any service without its
 * own entry. Stored in localStorage (WebView2 persists it in the app data
 * dir — single-user desktop). Read fresh per request so edits in Settings
 * apply immediately.
 *
 * On desktop, Settings → "Import from this PC" fills the map from the SAGE
 * repo's .mcp.json via the `import_mcp_tokens` Tauri command — the
 * "download it and it's already linked up" behavior.
 */

const TOKEN_KEY = "sage_session_token"; // legacy single-token (companion-compatible)
const TOKENS_KEY = "sage_mcp_tokens"; // per-service map {core: "...", business: "...", ...}

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setSessionToken(token: string): void {
  try {
    const trimmed = token.trim();
    if (trimmed) localStorage.setItem(TOKEN_KEY, trimmed);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
}

export function getServiceTokens(): Record<string, string> {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch {
    return {};
  }
}

export function setServiceTokens(map: Record<string, string>): void {
  try {
    const cleaned = Object.fromEntries(
      Object.entries(map)
        .map(([k, v]) => [k, (v ?? "").trim()])
        .filter(([, v]) => v),
    );
    localStorage.setItem(TOKENS_KEY, JSON.stringify(cleaned));
  } catch {
    /* storage unavailable */
  }
}

/** Token for one service: per-service entry first, legacy single token as fallback. */
export function getServiceToken(service: string): string | null {
  const map = getServiceTokens();
  return map[service] ?? getSessionToken();
}

/** True when running inside the Tauri desktop shell (vs plain browser/pages.dev). */
export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Pull worker bearers from the SAGE repo's .mcp.json on this PC.
 *  Desktop: Tauri command. Browser dev: dev-server middleware twin. */
export async function importTokensFromThisPc(): Promise<Record<string, string>> {
  let map: Record<string, string>;
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    map = await invoke<Record<string, string>>("import_mcp_tokens");
  } else if (import.meta.env.DEV) {
    const res = await fetch("/mcp-dev/tokens");
    if (!res.ok) throw new Error(`token import failed: HTTP ${res.status}`);
    map = await res.json();
  } else {
    throw new Error("Desktop only");
  }
  // Split out LLM chat keys (prefixed _) from MCP worker tokens.
  const workerMap: Record<string, string> = {};
  for (const [k, v] of Object.entries(map)) {
    if (k === "_openrouter") { try { localStorage.setItem("sage_openrouter_key", v); } catch { /* */ } }
    else if (k === "_anthropic") { try { localStorage.setItem("sage_anthropic_key", v); } catch { /* */ } }
    else workerMap[k] = v;
  }
  const existing = getServiceTokens();
  setServiceTokens({ ...existing, ...workerMap });
  return workerMap;
}
